import type { CardData, GrandSnatchState } from './types';
import type { CardEffectInstance, EffectKind } from './effects';

// Trigger-payload union. Each trigger carries the data its effects need.
export type EffectPayload =
  | { trigger: 'on_play'; card: CardData }
  | { trigger: 'on_table'; card: CardData }
  | { trigger: 'on_capture'; taker: CardData; takenCards: CardData[] }
  | { trigger: 'on_captured'; victim: CardData; takerCard: CardData; takerPlayerID: string }
  | { trigger: 'on_snatch'; card: CardData }
  | { trigger: 'on_draw'; card: CardData }
  | { trigger: 'on_resource_gained'; suit: string; amount: number; recipientID: string }
  | { trigger: 'on_game_start' }
  | { trigger: 'in_discard' }
  | { trigger: 'on_round_end'; roundCount: number };

export interface EffectContext {
  G: GrandSnatchState;
  playerID: string;          // owner of the carrier card (the card holding the effect)
  opponentID: string;
  payload: EffectPayload;
}

type EffectApply = (ctx: EffectContext, effect: CardEffectInstance, carrier: CardData) => void;

// Registry indexed by EffectKind. Each apply mutates G in-place (Immer-friendly).
// Pure: no Math.random, no Date.now. If randomness is needed in v2, a prng will
// be added to EffectContext.
export const EFFECT_REGISTRY: Record<EffectKind, EffectApply> = {
  // give_resources: add `params.amount` of `params.suit` to ctx.playerID's resources.
  // Used by drug 3 (on_play): +1 drug.
  give_resources: (ctx, effect, _carrier) => {
    const suit = effect.params.suit;
    const amount = effect.params.amount ?? 1;
    if (!suit) return;
    const p = ctx.G.players[ctx.playerID];
    p.resources[suit] = (p.resources[suit] || 0) + amount;
  },

  // take_only_singleton: declarative — read by resolveTake. Apply no-op.
  // (The trigger 'on_table' is set so the effect surfaces on the card the
  // moment it lands on the dropzone; the rule is then read declaratively.)
  take_only_singleton: () => {},

  // DEFENSIVE — on_captured: il captor (NON il proprietario originale) ottiene
  // un bonus (factor-1)*value della suit della vittima. Il drain di stage.onEnd
  // aggiunge poi il normale value, totalizzando factor*value.
  double_resources_when_taken: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_captured') return;
    const factor = effect.params.factor ?? 2;
    const bonus = (factor - 1) * carrier.value;
    if (bonus <= 0) return;
    const captor = ctx.G.players[ctx.payload.takerPlayerID];
    captor.resources[carrier.suit] = (captor.resources[carrier.suit] || 0) + bonus;
  },

  // OFFENSIVE (any play) — on_play: il proprietario riceve subito il bonus
  // appena gioca la carta, anche se la carta finirà sul tavolo senza prendere.
  // Il drain di stage.onEnd può poi aggiungere o no il normale value a seconda
  // di dove finisce la carta: in stagingArea del giocatore (se ha catturato)
  // → drain a lui; in dropzone (se on_table) → drain non avviene finché non
  // viene ripresa più avanti. Il bonus istantaneo da qui è indipendente da ciò.
  double_resources_when_played: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_play') return;
    const factor = effect.params.factor ?? 2;
    const bonus = (factor - 1) * carrier.value;
    if (bonus <= 0) return;
    // ctx.playerID = owner del carrier = il giocatore che ha appena giocato.
    const player = ctx.G.players[ctx.playerID];
    player.resources[carrier.suit] = (player.resources[carrier.suit] || 0) + bonus;
  },

  // OFFENSIVE on_capture — bonus solo se il play della carta porta a una
  // presa effettiva. Per on_capture, ctx.playerID = owner del taker = il
  // giocatore che ha appena catturato.
  double_resources_when_capturing: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_capture') return;
    const factor = effect.params.factor ?? 2;
    const bonus = (factor - 1) * carrier.value;
    if (bonus <= 0) return;
    const player = ctx.G.players[ctx.playerID];
    player.resources[carrier.suit] = (player.resources[carrier.suit] || 0) + bonus;
  },

  // BIDIREZIONALE — gestisce sia on_capture sia on_captured. Espanso a livello
  // di instantiateEffect in due CardEffectInstance distinti (uno per trigger)
  // che entrambi puntano a questa kind. Routes internamente sul trigger.
  // Semantica "chi possiede la carta": se la giochi e prendi, bonus a te;
  // se l'avversario te la cattura, bonus a lui; se finisce a tavolo, niente.
  double_resources_when_acquired: (ctx, effect, carrier) => {
    const factor = effect.params.factor ?? 2;
    const bonus = (factor - 1) * carrier.value;
    if (bonus <= 0) return;
    if (ctx.payload.trigger === 'on_capture') {
      const player = ctx.G.players[ctx.playerID];
      player.resources[carrier.suit] = (player.resources[carrier.suit] || 0) + bonus;
    } else if (ctx.payload.trigger === 'on_captured') {
      const captor = ctx.G.players[ctx.payload.takerPlayerID];
      captor.resources[carrier.suit] = (captor.resources[carrier.suit] || 0) + bonus;
    }
  },

  // STEAL — on_play. Ruba `params.amount` (default 1) della suit `params.suit`
  // (default = suit della carta) all'avversario, accreditandola al player.
  // Clampato al minimo tra amount e risorse correnti dell'avversario, mai
  // valori negativi. Niente effetto se l'avversario non ha la risorsa.
  steal_resources: (ctx, effect, _carrier) => {
    if (ctx.payload.trigger !== 'on_play') return;
    const suit = effect.params.suit ?? _carrier.suit;
    const wanted = effect.params.amount ?? 1;
    const opp = ctx.G.players[ctx.opponentID];
    const player = ctx.G.players[ctx.playerID];
    const stealable = Math.min(wanted, opp.resources[suit] || 0);
    if (stealable <= 0) return;
    opp.resources[suit] = (opp.resources[suit] || 0) - stealable;
    player.resources[suit] = (player.resources[suit] || 0) + stealable;
  },

  // ============ v2 — STATEFUL HANDLERS ============
  // Ticchettati da on_round_end (dispatchato in stage.onEnd). Lo `state`
  // mutato qui vive in G per la durata del match e non viene mai ripersistito.

  // decaying_value — on_round_end: la carta perde 1 valore (min 1) finché sta
  // su una dropzone. La merce si deteriora: il calo si ripercuote su resolveTake
  // (presa), scoring (value_count) e display, come voluto.
  decaying_value: (ctx, _effect, carrier) => {
    if (ctx.payload.trigger !== 'on_round_end') return;
    const onDropzone = ctx.G.dropzones.some(d => d.cards.some(c => c.id === carrier.id));
    if (!onDropzone) return;
    if (carrier.value > 1) carrier.value -= 1;
  },

  // time_bomb — on_round_end: scala la miccia mentre la carta è su una
  // dropzone. A 0 detona: rimuove dalla dropzone le carte bersaglio (tutte le
  // altre, o solo quelle dell'avversario del proprietario). La bomba resta e
  // si disinnesca (state.detonated) per non riesplodere ogni round.
  time_bomb: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_round_end') return;
    const dz = ctx.G.dropzones.find(d => d.cards.some(c => c.id === carrier.id));
    if (!dz) return; // ticchetta solo su una dropzone
    let st = effect.state;
    if (!st) { st = { fuse: 3 }; effect.state = st; }
    if (st.detonated) return;
    const fuse = (typeof st.fuse === 'number' ? st.fuse : 3) - 1;
    st.fuse = fuse;
    if (fuse > 0) return;
    st.detonated = true;
    const bombOwner = ctx.playerID;
    dz.cards = dz.cards.filter(c => {
      if (c.id === carrier.id) return true;                 // la bomba sopravvive
      if (effect.params.blast === 'enemy') {
        const cOwner = c.id.startsWith('p0') ? '0' : '1';
        return cOwner === bombOwner;                         // distrugge solo le avversarie
      }
      return false;                                         // blast 'all': distrugge tutto il resto
    });
  },

  // freeze — on_table: appena la carta viene calata, congela la carta
  // highest/lowest già presente sulla stessa dropzone. `frozenTurns` viene
  // letto da scopaAlg (carta non catturabile) e scalato in stage.onEnd. Il +1
  // compensa il decremento di questo stesso round.
  freeze: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_table') return;
    const dz = ctx.G.dropzones.find(d => d.cards.some(c => c.id === carrier.id));
    if (!dz) return;
    const others = dz.cards.filter(c => c.id !== carrier.id && (c.frozenTurns ?? 0) <= 0);
    if (others.length === 0) return;
    const pickLowest = effect.params.target === 'lowest';
    let target = others[0];
    for (const c of others) {
      if (pickLowest ? c.value < target.value : c.value > target.value) target = c;
    }
    target.frozenTurns = (effect.params.turns ?? 2) + 1;
    target.frozenBy = `${carrier.value} of ${carrier.suit}`;
  },

  // ripening_loot — on_round_end (su dropzone): banca +amount. on_captured: il
  // captor incassa tutto il bancato della suit della carta, in più del normale
  // value che il drain di stage.onEnd gli darà comunque.
  ripening_loot: (ctx, effect, carrier) => {
    let st = effect.state;
    if (!st) { st = { banked: 0 }; effect.state = st; }
    if (ctx.payload.trigger === 'on_round_end') {
      const onDropzone = ctx.G.dropzones.some(d => d.cards.some(c => c.id === carrier.id));
      if (!onDropzone) return;
      const amount = effect.params.amount ?? 1;
      st.banked = (typeof st.banked === 'number' ? st.banked : 0) + amount;
      return;
    }
    if (ctx.payload.trigger === 'on_captured') {
      const banked = typeof st.banked === 'number' ? st.banked : 0;
      if (banked <= 0) return;
      const captor = ctx.G.players[ctx.payload.takerPlayerID];
      const suit = effect.params.suit ?? carrier.suit;
      captor.resources[suit] = (captor.resources[suit] || 0) + banked;
      st.banked = 0;
    }
  },

  // ============ v3 — EFFETTI LEGATI AI BLOCCHI ============

  // stash_payout — on_round_end: paga una volta sola, nel round in cui sta per
  // arrivare la mano 2 (entrambe le mani vuote, mazzo del proprietario non
  // vuoto: on_round_end è dispatchato PRIMA del refill), se la carta è ancora
  // su una dropzone.
  stash_payout: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_round_end') return;
    const G = ctx.G;
    const hand2Incoming = G.players['0'].hand.length === 0 && G.players['1'].hand.length === 0
      && G.players[ctx.playerID].deck.length > 0;
    if (!hand2Incoming) return;
    if (!G.dropzones.some(d => d.cards.some(c => c.id === carrier.id))) return;
    const suit = effect.params.suit ?? carrier.suit;
    const p = G.players[ctx.playerID];
    p.resources[suit] = (p.resources[suit] || 0) + (effect.params.amount ?? 3);
  },

  // sleeper — on_draw: la carta guadagna +amount di valore (permanente per il
  // match: influisce su prese, scoring e risorse al drain).
  sleeper: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_draw') return;
    carrier.value += effect.params.amount ?? 1;
  },

  // seize_priority — on_table: il proprietario avrà la priorità su questa
  // dropzone nel round successivo. La priorità si inverte a fine round
  // (stage.onEnd), quindi se ORA ce l'ha l'avversario il flip gliela dà già;
  // altrimenti scambiamo le priorità delle due dropzone (restano opposte).
  seize_priority: (ctx, _effect, carrier) => {
    if (ctx.payload.trigger !== 'on_table') return;
    const G = ctx.G;
    const idx = G.dropzones.findIndex(d => d.cards.some(c => c.id === carrier.id));
    if (idx === -1) return;
    const dz = G.dropzones[idx];
    const other = G.dropzones[idx === 0 ? 1 : 0];
    if (dz.priorityPlayer === ctx.opponentID) return;
    const tmp = dz.priorityPlayer;
    dz.priorityPlayer = other.priorityPlayer;
    other.priorityPlayer = tmp;
  },

  // getaway — on_round_end: se è su una dropzone si sposta in coda all'altra.
  // state.movedRound evita il doppio spostamento nello stesso tick (il
  // dispatcher scansiona dz0 e poi dz1: la carta appena arrivata su dz1
  // verrebbe vista di nuovo). Una carta congelata non si muove.
  getaway: (ctx, effect, carrier) => {
    if (ctx.payload.trigger !== 'on_round_end') return;
    const round = ctx.payload.roundCount;
    if (!effect.state) effect.state = {};
    if (effect.state.movedRound === round) return;
    if ((carrier.frozenTurns ?? 0) > 0) return;
    const G = ctx.G;
    const idx = G.dropzones.findIndex(d => d.cards.some(c => c.id === carrier.id));
    if (idx === -1) return;
    const from = G.dropzones[idx];
    const to = G.dropzones[idx === 0 ? 1 : 0];
    const pos = from.cards.findIndex(c => c.id === carrier.id);
    const [moved] = from.cards.splice(pos, 1);
    to.cards.push(moved);
    effect.state.movedRound = round;
  },

  // held_charge — on_round_end (in mano): accumula i round trattenuti.
  // on_play: paga roundsHeld * amount della suit della carta al proprietario.
  held_charge: (ctx, effect, carrier) => {
    let st = effect.state;
    if (!st) { st = { roundsHeld: 0 }; effect.state = st; }
    if (ctx.payload.trigger === 'on_round_end') {
      // Di norma conta solo i round in mano; con countDeck (Syndicate High
      // Rollers) anche quelli passati nel mazzo in attesa della mano 2.
      const p = ctx.G.players[ctx.playerID];
      const held = p.hand.some(c => c.id === carrier.id)
        || (effect.params.countDeck === true && p.deck.some(c => c.id === carrier.id));
      if (!held) return;
      st.roundsHeld = (typeof st.roundsHeld === 'number' ? st.roundsHeld : 0) + 1;
      return;
    }
    if (ctx.payload.trigger === 'on_play') {
      const rounds = typeof st.roundsHeld === 'number' ? st.roundsHeld : 0;
      if (rounds <= 0) return;
      const amount = effect.params.amount ?? 1;
      const suit = effect.params.suit ?? carrier.suit;
      const player = ctx.G.players[ctx.playerID];
      player.resources[suit] = (player.resources[suit] || 0) + rounds * amount;
    }
  },
};
